import { useMutation } from '@apollo/react-hooks';
import { DetailLayoutView } from 'components/DetailView';
import React, { useContext, useState } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  Col, 
  Row,
  Spinner,
} from 'reactstrap';
import _ from 'lodash';
import styled from 'styled-components';
import { ToastContainer } from 'react-toastify';

import { errorNotify, successNotify } from 'utils/toaster';
import { Config } from '../../config';
import NewDetailView from 'components/DetailView/NewDetailScreen';
import { useHistory } from 'react-router-dom';
import { UserContext } from 'context/UserContext';
import { SendBroadcastMutation, DeleteBroadcastMetadataMutation } from 'graphql/chat';
import CustomSpinner from 'components/CustomSpinner';

const CreateBroadcast = () => {
  const history = useHistory();

  const { token, user, isAdmin } = useContext(UserContext);

  const [preview, setPreview] = useState('');
  const [imageUrlFile, setImageUrlFile] = useState();
  const [draftModal, setDraftModal] = useState(false);
  const [publishModal, setPublishModal] = useState(false);

  const [values, setValues] = useState({
    broadcastTitle: '',
    broadcastMessage: '',
    broadcastButtonLink: ''
  });

  const [sendBroadcastMessage, { loading: sendBroadcastLoading }] = useMutation(SendBroadcastMutation);
  const [deleteBroadcastMetadata] = useMutation(DeleteBroadcastMetadataMutation)

  const handleImageUpload = async ({ target: { files } }) => {
    const imageSizeLimit = 500;
    const reader = new FileReader();

    if (files) {
      reader.readAsDataURL(files[0]);
    }

    const imageSize = files[0]?.size / 1024;

    if (imageSize > imageSizeLimit) {
      return errorNotify(
        'The size of the image should be less than or equal to 500kb'
      );
    }

    setImageUrlFile(files[0]);

    reader.onloadend = function (e) {
      setPreview(reader.result);
    };
  };

  const handleBroadcast = async (arg) => {
    const {
      broadcastTitle,
      broadcastMessage,
      broadcastButtonLink
    } = values;

    if (!imageUrlFile) {
      return errorNotify(
        'Please upload playlist image or check the size of image uploaded'
      );
    }

    const form = new FormData();
    form.append('imageUrl', imageUrlFile);
    const publish = !!arg ? false : true;

    try {
      if (broadcastTitle && broadcastMessage && imageUrlFile && isAdmin) {
        const res = await fetch(`${Config.restApiUrl}/uploads/content/upload-image`, {
          method: 'POST',
          headers: {
            "Authorization": token,
          },
          body: form,
        });

        const response = await res.json();
        const imageUrl = response?.data?.imageUrl;

        if (imageUrl) {
          if (publish) {
            // clear broadcast metadata
            await deleteBroadcastMetadata({
              variables: {
                broadcasterId: user?.id
              }
            })
          }

          const res = await sendBroadcastMessage({
            variables: {
              objects: [{
                broadcastTitle: broadcastTitle.trim(),
                broadcastMessage: broadcastMessage.trim(),
                broadcastButtonLink: broadcastButtonLink.trim(),
                isBroadcastMessage: true,
                isBroadcastLive: publish,
                broadcastImage: imageUrl,
                userId: user?.id
              }],
            }
          });

          history.push('/broadcasts');
          
          if (publish) {
            successNotify('Broadcasted to all users');
          } else {
            successNotify('Saved to draft successfully');
          }
        } else {
          errorNotify('Error sending broadcast');
        }
      } else {
        errorNotify('Some fields are required');
      }
    } catch (error) {
      const err = error?.toString()?.split(':')[2]?.trim();
      errorNotify(err || 'Something went wrong');
    }
  };

  return (
    <Wrapper>
      <Row>
        <Col>
          <DetailLayoutView
            image={preview}
            onChange={handleImageUpload}
            uploadName='Broadcast (600px by 600px)'
            breadcrumbTitle='Broadcasts'
            breadcrumbItem='New Broadcast'
            breadcrumbRoute={"/broadcasts"}
          >
            <NewDetailView
              key1={{
                label: 'Broadcast Title',
                placeholder: 'Broadcast Title',
                name: 'broadcastTitle',
                status: 'create',
                type: 'input',
              }}
              key2={{
                label: 'Broadcast Message',
                placeholder: 'Broadcast Message',
                name: 'broadcastMessage',
                status: 'create',
                type: 'description',
              }}
              key3={{
                label: 'Broadcast Button Link',
                placeholder: 'Optional',
                name: 'broadcastButtonLink',
                status: 'create',
                type: 'input',
              }}
              action={{
                type: 'action',
                disabled: _.isEmpty(values.broadcastTitle) || _.isEmpty(values.broadcastMessage),
                actionText: {
                  btn2Text: 'Save to Draft',
                  btn3Text: 'Broadcast to all users',
                },
                action: {
                  btn2: () => setDraftModal(true),
                  btn3: () => setPublishModal(true),
                },
                actionLoading: {
                  btn3Loading: sendBroadcastLoading,
                },
              }}
              values={values}
              setValues={setValues}
              setDraftModal={() => setDraftModal(true)}
            />
          </DetailLayoutView>
        </Col>
      </Row>

      {/* draft publishModal */}
			<Modal isOpen={draftModal} centered={true}>
        <ModalBody>
          <p>
            Are you sure you want to save {values.title?.toUpperCase()} to Draft
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' onClick={() => setDraftModal(false)}>
            No
          </Button>{' '}
          <Button
            color='primary'
            onClick={() => {
              handleBroadcast("draft");
              setDraftModal(false);
            }}
          >
            Yes
          </Button>
        </ModalFooter>
			</Modal>

      {/* publish publishModal */}
      <Modal isOpen={publishModal} centered={true}>
        <ModalBody>
          <p>Are you sure you want to broadcast to all users?</p>
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' onClick={() => setPublishModal(false)}>
            No
          </Button>{' '}
          <Button
            color='primary'
            onClick={() => {
              handleBroadcast();
              setPublishModal(false);
            }}
          >
            {sendBroadcastLoading ? (
              <CustomSpinner />
            ) : (
              'Yes'
            )}
          </Button>
        </ModalFooter>
      </Modal>

      <ToastContainer />
    </Wrapper>
  );
};

export const Wrapper = styled.div`
  margin-top: 10vh;
`;

export default CreateBroadcast;
